import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useApiNoSqlContext } from 'src/modules/contexts/ApiNoSqlContext';
import { ApiError, UtilityOptional, UtilityTemplateBase } from 'src/modules/generated-api-clients/no-sql';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { AxiosError } from 'axios';
import { MUTATION_KEYS, QUERY_KEYS } from './enums';
import { logApiError, logAxiosError, logError, logRequest } from './helpers';

interface ReadAllUtilityCompaniesParams extends ReadSingleUtilityCompanyParams {
	skip?: number;
	limit?: number;
}

interface ReadSingleUtilityCompanyParams {
	id?: string;
	sqlId?: string;
	invoiceCompanyName?: string;
	project?: string;
}

interface PutUtilityCompanyUtilityTemplateParams {
	id: string;
	requestBody: UtilityOptional;
}

/**
 * Custom hook to fetch a list of utility templates for a utility company.
 * Endpoint: GET '/api/v2/read/list/utility-template'
 * @param params - An object containing the following properties:
 *   - id: The utility ID (optional).
 *   - displayName: The display name (optional).
 *   - country: The country (optional).
 *   - stateProvince: The state/province (optional).
 *   - city: The city (optional).
 *   - postalCode: The postal code (optional).
 *   - project: Query param string with comma-separated field names as they appear in the database (optional).
 *   - skip: The number of records to skip (optional).
 *   - limit: The maximum number of records to return (optional).
 * @returns Either an array of objects each representing a Utility template or undefined.
 */
export const useQueryListAllUtilityCompany = (params?: ReadAllUtilityCompaniesParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	const queryKey =
		params === undefined
			? [QUERY_KEYS.utilities.AllUtilities, user]
			: [QUERY_KEYS.utilities.AllUtilities, params, user];

	const request = async () => {
		try {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response =
				params === undefined
					? await client.utilityCompanyUtilityTemplate.listAllUtilityCompanyApiV2ReadListUtilityTemplateGet()
					: await client.utilityCompanyUtilityTemplate.listAllUtilityCompanyApiV2ReadListUtilityTemplateGet(
							params.id,
							params.sqlId,
							params.invoiceCompanyName,
							params.project,
							//params.skip,
							//params.limit
					  );
			// logRequest(params, 'GET', QUERY_KEYS.utilities.AllUtilities, response);
			return response;
		} catch (error: unknown) {
			if (error instanceof ApiError) {
				logApiError(error);
			} else if (error instanceof AxiosError) {
				logAxiosError(error);
			} else {
				logError(error, 'Error fetching utility template list');
			}
			throw error;
		}
	};

	return useQuery<UtilityOptional[] | undefined, ApiError>(queryKey, request, {
		enabled: !!user.attributes?.email
	});
};

/**
 * Custom hook to fetch a single utility template for a utility company.
 * Endpoint: GET '/api/v2/read/single/utility-template'
 * @param params - An object containing the following properties:
 *   - id: The utility ID (optional).
 *   - displayName: The display name (optional).
 *   - country: The country (optional).
 *   - stateProvince: The state/province (optional).
 *   - city: The city (optional).
 *   - postalCode: The postal code (optional).
 *   - project: Query param string with comma-separated field names as they appear in the database (optional).
 * @returns An object representing a Utility template or undefined.
 */
export const useReadUtilityNoSql = (params: ReadSingleUtilityCompanyParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	const queryKey =
		params === undefined
			? [QUERY_KEYS.utilities.SingleUtility, user]
			: [QUERY_KEYS.utilities.SingleUtility, params, user];

	const request = async () => {
		try {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response =
				params === undefined
					? await client.utilityCompanyUtilityTemplate.readOneUtilityCompanyApiV2ReadSingleUtilityTemplateGet()
					: await client.utilityCompanyUtilityTemplate.readOneUtilityCompanyApiV2ReadSingleUtilityTemplateGet(
							params.id,
							params.sqlId,
							params.invoiceCompanyName,
							params.project
					  );
			// logRequest(params, 'GET', QUERY_KEYS.utilities.AllUtilities, response);
			return response;
		} catch (error: unknown) {
			if (error instanceof ApiError) {
				logApiError(error);
			} else if (error instanceof AxiosError) {
				logAxiosError(error);
			} else {
				logError(error, 'Error fetching utility template');
			}
			throw error;
		}
	};

	return useQuery<UtilityOptional | undefined, ApiError>(queryKey, request, {
		enabled: !!user.attributes?.email
	});
};

//
/**
 * Custom hook to create a new utility template for a utility company.
 * Endpoint: POST '/api/v2/create/utility-template'
 * @returns A mutation function to create the utility template.
 */
export const useMutationPostUtilityCompanyUtilityTemplate = () => {
	const queryClient = useQueryClient();
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	return useMutation<UtilityOptional | undefined, ApiError, UtilityTemplateBase>(
		(requestBody: UtilityTemplateBase) => {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response =
				client.utilityCompanyUtilityTemplate.createUtilityCompanyApiV2CreateUtilityTemplatePost(requestBody);
			logRequest(requestBody, 'POST', MUTATION_KEYS.utilities.CreateUtility, response);
			return response;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS.utilities.AllUtilities);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error creating utility template');
				}
			}
		}
	);
};

/**
 * Custom hook to update an existing utility template for a utility company.
 * Endpoint: PUT '/api/v2/update/utility-template/{id}'
 * @returns A mutation function to update the utility template.
 */
export const useUpdateUtilityNoSql = () => {
	const queryClient = useQueryClient();
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	return useMutation<any, ApiError, PutUtilityCompanyUtilityTemplateParams>(
		(params: PutUtilityCompanyUtilityTemplateParams) => {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			if (params.id === undefined) {
				throw new Error('Utility Company _id cannot be undefined');
			}
			const response = client.utilityCompanyUtilityTemplate.updateUtilityCompanyApiV2UpdateUtilityTemplateIdPatch(
				params.id,
				params.requestBody
			);
			logRequest(params, 'PUT', MUTATION_KEYS.utilities.UpdateUtility, response);
			return response;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS.utilities.AllUtilities);
				queryClient.invalidateQueries(QUERY_KEYS.utilities.SingleUtility);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error updating utility template');
				}
			}
		}
	);
};
